import type { ExpertSummary } from "../../api";

/** Markdown article preview. Until the api ships a per-artifact content
 *  endpoint, the caller can pass `content` directly (rendered as preformatted
 *  markdown source) — we'll plug in a real markdown renderer once content is
 *  fetchable. The visual contract (typography, expert-tinted blockquote,
 *  hairline H2 underline) is fixed regardless of the source.
 */
export function MdPreview({
  expert,
  content,
}: {
  expert: ExpertSummary;
  content?: string;
}) {
  const c = expert.color_token;
  return (
    <article
      style={{
        maxWidth: 760,
        margin: "0 auto",
        fontSize: 15,
        lineHeight: 1.65,
        color: "var(--ink-2)",
      }}
    >
      <style>{`
        article h1, article h2, article h3 {
          color: var(--ink); font-weight: 500; letter-spacing: -.01em;
        }
        article h2 {
          font-size: 20px; margin-top: 26px; margin-bottom: 8px;
          padding-bottom: 6px; border-bottom: 1px solid var(--rule);
        }
        article p { margin: 10px 0; }
        article code.mono {
          background: var(--paper-2); padding: 1px 5px; border-radius: 3px;
          font-size: 13px;
        }
        article ul, article ol { padding-left: 22px; }
        article li { margin: 5px 0; }
        article blockquote {
          margin: 16px 0; padding: 10px 14px;
          border-left: 3px solid var(--${c});
          background: var(--${c}-tint);
          border-radius: 4px; font-style: italic; color: var(--ink-2);
        }
        article pre {
          background: var(--paper-2); padding: 12px; border-radius: 6px;
          overflow: auto; font-size: 12.5px; line-height: 1.55;
          font-family: var(--mono);
        }
      `}</style>
      {content ? (
        <pre>{content}</pre>
      ) : (
        <SamplePlaceholder expert={expert} />
      )}
    </article>
  );
}

function SamplePlaceholder({ expert }: { expert: ExpertSummary }) {
  return (
    <>
      <h1
        className="serif"
        style={{ fontSize: 32, fontWeight: 400, margin: 0 }}
      >
        Sample preview
      </h1>
      <p className="dim">
        Per-artifact content will render here once the api ships a
        <code className="mono"> GET /v1/jobs/&lt;id&gt;/artifact</code>{" "}
        endpoint. Today the bundle download is the canonical way to read
        full artifacts.
      </p>
      <h2>Section</h2>
      <p>
        Body content from <strong>{expert.display_name}</strong> would
        render here with full markdown typography — headings, lists, code
        blocks, and pull-quotes.
      </p>
      <ul>
        <li>First finding from this expert.</li>
        <li>Second finding tied to a transcript moment at 00:01:15.</li>
        <li>
          Third item with <code className="mono">inline.code()</code>.
        </li>
      </ul>
      <blockquote>
        Pull-quote from the transcript: a moment the expert flagged as
        decision-shaping.
      </blockquote>
    </>
  );
}
